import { getDistance } from "geolib";

export interface SupplierMetrics {
  distanceKm: number;
  coverage: number | undefined;
  isAssociated: boolean;
  supplierName: string;
  supplierId: string;
  totalHits: number;
  fulfilledCount: number;
  fulfilledPercent?: number;
  totalPartsQuantity: number;
  totalPartPercentage: number;
  supplierCity?: string;
}

export const calculateDistance = (
  userLatitude: number | undefined,
  userLongitude: number | undefined,
  coords: string | undefined,
): number => {
  if (!coords || userLatitude === undefined || userLongitude === undefined) {
    return Infinity;
  }

  const [lat, lng] = coords.split(",").map(Number);
  if (isNaN(lat) || isNaN(lng)) return Infinity;

  const meters = getDistance(
    { latitude: userLatitude, longitude: userLongitude },
    { latitude: lat, longitude: lng },
  );
  return meters / 1000;
};

export const calculateCoverage = (
  found: number,
  totalParts: number,
): number | undefined => {
  return totalParts > 0 ? Math.floor((found / totalParts) * 100) : undefined;
};

export const calculateFulfilledPercent = (
  found: number,
  totalParts: number,
): number | undefined => {
  return totalParts > 0 ? Math.floor((found / totalParts) * 100) : undefined;
};

export const calculateTotalPartsQuantity = (
  supplierParts: any[],
  preFacetedPartNumbersMap: Map<string, number>,
): number => {
  return supplierParts.reduce((sum: number, supplierPart: any) => {
    const orderedQuantity =
      preFacetedPartNumbersMap.get(supplierPart.rootPartNumber) ?? 0;
    const available = supplierPart.familyQuantity ?? 0;
    return sum + Math.min(available, orderedQuantity);
  }, 0);
};

export const calculateQuantityFulfilledPercent = (
  totalPartsQuantity: number,
  totalRequestedQuantity: number,
): number => {
  return totalRequestedQuantity > 0
    ? Math.floor((totalPartsQuantity / totalRequestedQuantity) * 100)
    : 0;
};

export type BuildSupplierMetricsOptions = {
  distanceKmOverride?: number;
  fulfilledCountOverride?: number;
  supplierCity?: string;
};

export const buildSupplierMetrics = (
  hit: { found: number; hits: any[] },
  supplierName: string,
  supplierId: string,
  coords: string,
  userLatitude: number | undefined,
  userLongitude: number | undefined,
  customerSupplierAssociations: string[],
  preFacetedPartNumbers: any[],
  preFacetedPartNumbersMap: Map<string, number>,
  totalRequestedQuantity: number,
  options?: BuildSupplierMetricsOptions,
): SupplierMetrics | null => {
  const isAssociated = customerSupplierAssociations.includes(supplierId);
  const distanceKm =
    options?.distanceKmOverride !== undefined
      ? options.distanceKmOverride
      : calculateDistance(userLatitude, userLongitude, coords);
  const totalParts = preFacetedPartNumbers.length;
  const fulfilledCount =
    options?.fulfilledCountOverride !== undefined
      ? options.fulfilledCountOverride
      : hit.found || 0;
  const coverage = calculateCoverage(hit.found, totalParts);
  const fulfilledPercent = calculateFulfilledPercent(
    fulfilledCount,
    totalParts,
  );
  const totalPartsQuantity = calculateTotalPartsQuantity(
    hit.hits,
    preFacetedPartNumbersMap,
  );
  const quantityFulfilledPercent = calculateQuantityFulfilledPercent(
    totalPartsQuantity,
    totalRequestedQuantity,
  );

  const supplierCity =
    options?.supplierCity !== undefined
      ? options.supplierCity
      : hit.hits?.[0]?.city;

  return {
    distanceKm,
    coverage,
    isAssociated,
    supplierName,
    supplierId,
    totalHits: hit.found,
    fulfilledCount,
    fulfilledPercent,
    totalPartsQuantity: totalPartsQuantity,
    totalPartPercentage: quantityFulfilledPercent,
    supplierCity,
  };
};
